import { ChainId } from "@pancakeswap/chains"
import axios from "axios"
import { MORALIS_API, MORALIS_API_KEY } from "config/constants/endpoints"
import { PEGGING_TOKENS } from "config/constants/peg"
import { gql } from "graphql-request"
import { useMemo } from "react"
import useSWRImmutable from "swr/immutable"
import { statsClients } from "utils/graphql"
import { TokenFilter } from "../components/FilterModal"

interface TokenData {
    address: string
    name: string
    symbol: string
    decimals: number
    price: number
    priceChange: number
    liquidity: number
    volume: number
    volume1d: number
    // volume7d: number
    marketCap: number
    marketCapChange: number
}

export function useAllTokens(version: string, filter: TokenFilter, sortField: string = 'volume1d', sortDesc: boolean = true, page: number = 0) {
    const filters = [
        version ? `{ version: $version }` : '{ version_not: "v2" }',
        filter?.query ? 
            `{
                or: [
                    { token_: { id: $query } },
                    { token_: { name_contains_nocase: $query } },
                    { token_: { symbol_contains_nocase: $query } },
                ]
            }` : undefined,
        ...filter?.ranges.flatMap(range => 
            [
                range.min ? `{ ${range.field}_gte: "${range.min}" }` : undefined,
                range.max ? `{ ${range.field}_lte: "${range.max}" }` : undefined,
            ].filter(Boolean)
        )
    ].filter(Boolean)
    const { data, status } = useSWRImmutable(
        ['info/tokens/all', version, filter, sortField, sortDesc ? 'desc' : 'asc', page],
        async () => {
            const where = filters ? `
                where: {
                    and: [${filters.join(',')}]
                }
            ` : ''
            const { tokens } = await statsClients[ChainId.PULSE].request<{ tokens: any[] }>(gql`
                query ($version: String, $query: String) {
                    tokens: tokenDatas(${where}, orderBy: ${sortField}, orderDirection: ${sortDesc ? 'desc' : 'asc'}, first: 20, skip: ${page * 20}) {
                        liquidity
                        price
                        price1d
                        price7d
                        marketCap
                        marketCap1d
                        marketCap7d
                        volume
                        volume1d
                        volume7d
                        token {
                            id
                            symbol
                            name
                            decimals
                        }
                    }
                }
            `, { query: filter?.query, version })
            return tokens.map(token => ({
                address: token.token.id,
                name: token.token.name,
                symbol: token.token.symbol,
                decimals: Number(token.token.decimals),
                liquidity: Number(token.liquidity),
                price: Number(token.price),
                priceChange: Number(token.price) > 0 ? Number(token.price1d) * 100 / Number(token.price) : 0,
                marketCap: Number(token.marketCap),
                marketCapChange: Number(token.marketCap) > 0 ? Number(token.marketCap1d) * 100 / Number(token.marketCap) : 0,
                volume: Number(token.volume),
                volume1d: Number(token.volume1d),
            }) as TokenData)
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return useMemo(() => {
        return { data, status }
    }, [data, status])
}

export function useToken(address: string) {
    const { data: tokenDatas } = useTokens([address])
    if (tokenDatas)
        return tokenDatas[0]
    return undefined
}

export function useTokens(addresses: string[]) {
    const { data, status } = useSWRImmutable(
        ['info/tokens/addresses', addresses],
        async () => {
            const { tokenDatas } = await statsClients[ChainId.PULSE].request<{ tokenDatas: any[] }>(gql`
                query($addresses: [String!]!) {
                    tokenDatas(where: { id_in: $addresses, version: "v3" }) {
                        liquidity
                        price
                        price1d
                        price7d
                        marketCap
                        marketCap1d
                        marketCap7d
                        volume
                        volume1d
                        volume7d
                        token {
                            id
                            symbol
                            name
                            decimals
                        }
                    }
                }
            `, { addresses: addresses.filter(Boolean).map(addr => addr.toLowerCase()) })
            return tokenDatas.map(token => ({
                address: token.token?.id,
                name: token.token?.name,
                symbol: token.token?.symbol,
                decimals: Number(token.token?.decimals ?? 18),
                liquidity: Number(token.liquidity),
                price: Number(token.price),
                priceChange: Number(token.price) > 0 ? Number(token.price1d) * 100 / Number(token.price) : 0,
                marketCap: Number(token.marketCap),
                marketCapChange: Number(token.marketCap) > 0 ? Number(token.marketCap1d) * 100 / Number(token.marketCap) : 0,
                volume: Number(token.volume),
                volume1d: Number(token.volume1d),
            } as TokenData))
        },
        {
            keepPreviousData: true,
            refreshInterval: 10_000
        },
    )
    return useMemo(() => {
        return { data, status }
    }, [data, status])
}

export function usePeggingPrices() {
    const { data, status } = useSWRImmutable(
        ['info/tokens/pegging'],
        async () => {
            const { data: prices } = await axios.post(`${MORALIS_API}/erc20/prices?chain=pulse`,
                {
                  tokens: Object.values(PEGGING_TOKENS).map(t => ({ token_address: t }))
                },
                {
                    headers: {
                        'accept': 'application/json',
                        'X-API-Key': MORALIS_API_KEY
                    }
                }
            ).catch(() => ({ data: [] }))
            return Object.fromEntries(
                Object.entries(PEGGING_TOKENS).map(([k, v]) =>
                    [k, Number(prices.find(p => p.tokenAddress.toLowerCase() === v.toLowerCase())?.usdPrice ?? 0)]
                )
            )
        },
        {
            keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
    return useMemo(() => {
        return { data, status }
    }, [data, status])
}